import { useAchievementTypes, useReduxState } from 'hooks';
import {
  DisplayAttachments,
  DisplayFormattedDate,
  ModalButton
} from 'components/common';
import {
  Box,
  BoxExpandable,
  BoxOverflowWrapper,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import { StatCount } from 'components/common/stats';
import { AchievementRenderAddedBy, AchievementCompletionItemsRender } from '.';

import type {
  Achievement,
  UserPreviousAchievementCompletion
} from 'shared-types';

interface Props {
  achievement: Pick<Achievement, 'title' | 'type'>;
  previousCompletions: UserPreviousAchievementCompletion[];
  style?: React.CSSProperties;
  userId: number;
}

const AchievementUserPreviousCompletions = ({
  achievement,
  previousCompletions,
  style,
  userId
}: Props) => {
  const { messages } = useReduxState();

  const { hasExpiration, hasStartedAt } = useAchievementTypes({
    type: achievement?.type
  });

  return !previousCompletions?.length ? null : (
    <Box padding={0} style={style}>
      <BoxExpandable
        title={
          <>
            {messages.completionsPreviousCompletions}
            <StatCount
              isLarge={true}
              style={{ display: 'inline-block', marginLeft: 5 }}
              value={previousCompletions?.length || 0}
            />
          </>
        }
      >
        <BoxOverflowWrapper>
          <BoxTable>
            <BoxTableHeader>
              <BoxTableCell>{messages.commonTitle}</BoxTableCell>
              <BoxTableCell>{messages.commonAddedBy}</BoxTableCell>
              {hasStartedAt ? (
                <BoxTableCell>{messages.commonStartDate}</BoxTableCell>
              ) : null}
              <BoxTableCell>{messages.completionsCompletionDate}</BoxTableCell>
              {hasExpiration ? (
                <BoxTableCell>{messages.commonExpirationDate}</BoxTableCell>
              ) : null}
              <BoxTableCell></BoxTableCell>
            </BoxTableHeader>

            {previousCompletions.map((completion) => {
              const {
                addedBy,
                attachments,
                canSeeAttachments,
                canSeeCompletionItems,
                completionDate,
                completionItemsClearedDate,
                comments,
                expirationDate,
                id,
                isConfidential,
                startedAtDate
              } = completion.achievementData || {};

              return (
                <BoxTableRow key={id}>
                  <BoxTableCell>{achievement.title}</BoxTableCell>

                  <BoxTableCell>
                    <AchievementRenderAddedBy
                      addedBy={addedBy}
                      enableLink={false}
                    />
                  </BoxTableCell>

                  {hasStartedAt ? (
                    <BoxTableCell>
                      <DisplayFormattedDate date={startedAtDate} />
                    </BoxTableCell>
                  ) : null}

                  <BoxTableCell>
                    <DisplayFormattedDate date={completionDate} />
                  </BoxTableCell>

                  {hasExpiration ? (
                    <BoxTableCell>
                      <DisplayFormattedDate date={expirationDate} />
                    </BoxTableCell>
                  ) : null}

                  <BoxTableCell>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <DisplayAttachments
                        attachments={attachments}
                        isConfidential={!canSeeAttachments}
                        hideTitle={true}
                        useModalButtonPropsForTable={true}
                      />

                      {comments ? (
                        <ModalButton
                          aria-label={messages.commonComment}
                          data-tooltip={messages.commonComment}
                          category="transparent"
                          icon={['far', 'comment']}
                          iconStyle={{ fontSize: '1.5rem' }}
                          modalProps={{
                            closeButton: true,
                            title: messages.commonComment
                          }}
                          size="small"
                        >
                          <div>{comments}</div>
                        </ModalButton>
                      ) : null}

                      <AchievementCompletionItemsRender
                        authorized={canSeeCompletionItems}
                        cleared={completionItemsClearedDate}
                        confidential={isConfidential}
                        items={completion.achievementCompletionItems || []}
                        renderInModal={true}
                        renderInModalProps={{
                          style: {
                            borderRadius: 20,
                            fontSize: '1.2rem',
                            fontWeight: 600,
                            padding: '3px 9px 4px 7px'
                          }
                        }}
                        targetUserId={userId}
                        type={achievement.type}
                      />
                    </div>
                  </BoxTableCell>
                </BoxTableRow>
              );
            })}
          </BoxTable>
        </BoxOverflowWrapper>
      </BoxExpandable>
    </Box>
  );
};

export default AchievementUserPreviousCompletions;
